/** First-login nickname setup page. */

import { NicknameSelectionModal } from "@/components/NicknameSelectionModal";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { consumePostLoginPath } from "@/lib/auth-navigation";
import type { StateCode } from "@shared/regions";
import { MapPin, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";

export default function NicknameSetupPage() {
  const {
    user,
    userProfile,
    isAuthenticated,
    loading: authLoading,
    refetchProfile,
  } = useAuth();
  const {
    selectedRegion,
    setSelectedRegion,
    isLoading: regionLoading,
  } = useRegion();
  const [, setLocation] = useLocation();
  const [showModal, setShowModal] = useState(false);
  const completingRef = useRef(false);
  const needsRegion = selectedRegion === "ALL";

  const regionOptions: Array<{
    code: Exclude<StateCode, "ALL">;
    city: string;
    state: string;
  }> = [
    { code: "NSW", city: "시드니", state: "NSW" },
    { code: "VIC", city: "멜번", state: "VIC" },
    { code: "QLD", city: "브리즈번", state: "QLD" },
    { code: "WA", city: "퍼스", state: "WA" },
    { code: "SA", city: "애들레이드", state: "SA" },
    { code: "ACT", city: "캔버라", state: "ACT" },
    { code: "TAS", city: "호바트", state: "TAS" },
    { code: "NT", city: "다윈", state: "NT" },
  ];

  useEffect(() => {
    if (!authLoading && (!user || !isAuthenticated)) {
      setLocation("/login");
      return;
    }

    if (
      userProfile?.nicknameRequired === false &&
      !completingRef.current
    ) {
      setLocation(consumePostLoginPath());
    }
  }, [authLoading, isAuthenticated, setLocation, user, userProfile]);

  useEffect(() => {
    if (!regionLoading && !needsRegion) {
      setShowModal(true);
    }
  }, [needsRegion, regionLoading]);

  if (
    !user ||
    !isAuthenticated ||
    authLoading ||
    !userProfile ||
    regionLoading
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">계정 정보를 확인하고 있어요...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/5 via-background to-primary/5">
      <div className="w-full max-w-2xl space-y-6">
        <Card className="border-2 border-primary/20 shadow-lg">
          <CardHeader className="text-center space-y-4 pb-4">
            <div className="flex justify-center">
              <div className="rounded-full bg-primary/10 p-4">
                <Sparkles className="h-12 w-12 text-primary" />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold">반가워요! 🎉</CardTitle>
            <CardDescription className="text-lg space-y-2">
              <p className="font-medium text-foreground">
                동네에서 사용할 별명 하나만 고르면 바로 시작할 수 있어요.
              </p>
              <p className="text-muted-foreground">
                지역 + 형용사 + 호주 동물 형식으로 실명 대신 안전하게
                활동합니다.
              </p>
            </CardDescription>
          </CardHeader>
          <CardContent>
            {needsRegion ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-2 font-medium text-foreground">
                  <MapPin className="h-4 w-4 text-primary" />
                  주로 활동할 지역을 선택해주세요
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {regionOptions.map((region) => (
                    <button
                      key={region.code}
                      type="button"
                      className="rounded-lg border bg-background px-3 py-3 text-left transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      onClick={() => {
                        setSelectedRegion(region.code);
                        setShowModal(true);
                      }}
                    >
                      <span className="block font-medium text-foreground">
                        {region.city}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {region.state}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="text-center text-xs text-muted-foreground">
                  나중에 언제든 지역 설정에서 바꿀 수 있어요.
                </p>
              </div>
            ) : (
              <div className="text-center text-sm text-muted-foreground">
                첫 번째 추천 이름이 미리 선택되어 있어 한 번만 눌러도 됩니다.
              </div>
            )}
          </CardContent>
        </Card>

        <NicknameSelectionModal
          open={showModal}
          onOpenChange={(open) => {
            if (open) setShowModal(true);
          }}
          onSuccess={async () => {
            completingRef.current = true;
            await refetchProfile();
            setShowModal(false);
            setLocation(consumePostLoginPath());
          }}
          required
          currentNickname={
            userProfile.nicknameDisplay || userProfile.nickname || undefined
          }
        />
      </div>
    </div>
  );
}
